import {
  LayoutDashboard, Users, PiggyBank, HandCoins, Receipt,
  CalendarDays, BarChart3,   Briefcase, Bot, Settings,
  UserCog, Shield, FileSpreadsheet, ScrollText,
  type LucideIcon,
} from "lucide-react";

export interface PermissionAction {
  id: string;
  label: string;
}

export interface ModuleDef {
  id: string;
  label: string;
  actions: PermissionAction[];
}

export interface CategoryDef {
  name: string;
  icon: LucideIcon;
  modules: ModuleDef[];
}

export const PERMISSION_CATEGORIES: CategoryDef[] = [
  {
    name: "Core",
    icon: LayoutDashboard,
    modules: [
      {
        id: "dashboard",
        label: "Dashboard",
        actions: [
          { id: "view", label: "View" },
          { id: "analytics", label: "Analytics" },
        ],
      },
      {
        id: "members",
        label: "Member Management",
        actions: [
          { id: "view", label: "View" },
          { id: "create", label: "Create" },
          { id: "edit", label: "Edit" },
          { id: "delete", label: "Delete" },
          { id: "approve", label: "Approve" },
          { id: "export", label: "Export" },
        ],
      },
      {
        id: "contributions",
        label: "Savings & Contributions",
        actions: [
          { id: "view", label: "View" },
          { id: "create", label: "Create" },
          { id: "edit", label: "Edit" },
          { id: "delete", label: "Delete" },
          { id: "verify", label: "Verify" },
          { id: "export", label: "Export" },
        ],
      },
      {
        id: "loans",
        label: "Loans & Payments",
        actions: [
          { id: "view", label: "View" },
          { id: "create", label: "Create" },
          { id: "edit", label: "Edit" },
          { id: "delete", label: "Delete" },
          { id: "approve", label: "Approve" },
          { id: "reject", label: "Reject" },
          { id: "verify", label: "Verify" },
          { id: "export", label: "Export" },
        ],
      },
    ],
  },
  {
    name: "Operations",
    icon: Briefcase,
    modules: [
      {
        id: "expenses",
        label: "Expenses & Bills",
        actions: [
          { id: "view", label: "View" },
          { id: "create", label: "Create" },
          { id: "edit", label: "Edit" },
          { id: "delete", label: "Delete" },
          { id: "approve", label: "Approve" },
          { id: "export", label: "Export" },
        ],
      },
      {
        id: "meetings",
        label: "Meetings & Attendance",
        actions: [
          { id: "view", label: "View" },
          { id: "create", label: "Create" },
          { id: "edit", label: "Edit" },
          { id: "delete", label: "Delete" },
          { id: "export", label: "Export" },
        ],
      },
      {
        id: "payroll",
        label: "HR Payroll",
        actions: [
          { id: "view", label: "View" },
          { id: "create", label: "Create" },
          { id: "edit", label: "Edit" },
          { id: "delete", label: "Delete" },
          { id: "export", label: "Export" },
          { id: "import", label: "Import" },
        ],
      },
    ],
  },
  {
    name: "Reporting",
    icon: BarChart3,
    modules: [
      {
        id: "reports",
        label: "Reports & Analytics",
        actions: [
          { id: "view", label: "View" },
          { id: "export", label: "Export" },
          { id: "print", label: "Print" },
        ],
      },
    ],
  },
  {
    name: "Administration",
    icon: Settings,
    modules: [
      {
        id: "settings",
        label: "System Settings",
        actions: [
          { id: "view", label: "View" },
          { id: "edit", label: "Edit" },
          { id: "manage", label: "Manage" },
        ],
      },
      {
        id: "users",
        label: "Users & Roles",
        actions: [
          { id: "view", label: "View" },
          { id: "create", label: "Create" },
          { id: "edit", label: "Edit" },
          { id: "delete", label: "Delete" },
          { id: "manage", label: "Manage" },
        ],
      },
      {
        id: "ai",
        label: "AI Assistant",
        actions: [
          { id: "view", label: "View" },
        ],
      },
    ],
  },
];

export const ALL_RESOURCES = PERMISSION_CATEGORIES.flatMap((c) =>
  c.modules.map((m) => m.id)
);

export const ACTIONS_LABELS: Record<string, string> = {
  view: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  approve: "Approve",
  reject: "Reject",
  verify: "Verify",
  export: "Export",
  print: "Print",
  import: "Import",
  manage: "Manage",
  analytics: "Analytics",
};

export const DEPENDENCIES: Record<string, string[]> = {
  "members.edit": ["members.view"],
  "members.delete": ["members.view"],
  "members.approve": ["members.view"],
  "contributions.edit": ["contributions.view"],
  "contributions.delete": ["contributions.view"],
  "contributions.verify": ["contributions.view"],
  "loans.edit": ["loans.view"],
  "loans.delete": ["loans.view"],
  "loans.approve": ["loans.view"],
  "loans.reject": ["loans.view"],
  "loans.verify": ["loans.view"],
  "expenses.edit": ["expenses.view"],
  "expenses.delete": ["expenses.view"],
  "expenses.approve": ["expenses.view"],
  "meetings.edit": ["meetings.view"],
  "meetings.delete": ["meetings.view"],
  "payroll.edit": ["payroll.view"],
  "payroll.delete": ["payroll.view"],
  "settings.edit": ["settings.view"],
  "settings.manage": ["settings.view"],
  "users.edit": ["users.view"],
  "users.delete": ["users.view"],
  "users.manage": ["users.view"],
};

export const ALL_ACTIONS = [...new Set(
  PERMISSION_CATEGORIES.flatMap((c) =>
    c.modules.flatMap((m) => m.actions.map((a) => a.id))
  )
)];
